import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex items-center text-xs sm:text-sm text-black" }
const _hoisted_2 = { class: "font-medium" }
const _hoisted_3 = {
  key: 0,
  class: "ml-2 text-black"
}
const _hoisted_4 = { class: "flex items-center gap-1 sm:gap-2 flex-wrap" }
const _hoisted_5 = {
  key: 0,
  class: "hidden sm:flex items-center gap-1"
}
const _hoisted_6 = ["value"]
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "flex items-center gap-0.5 flex-wrap" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { class: "flex items-center gap-0.5 flex-wrap" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["disabled"]
const _hoisted_14 = ["disabled"]

import { computed } from 'vue'

interface Props {
  totalItems: number
  currentPage: number
  itemsPerPage: number
  variant?: 'dashboard' | 'product' | 'default'
  itemLabel?: string
  showItemsPerPage?: boolean
  showFirstLastButtons?: boolean
  maxVisiblePages?: number
  itemsPerPageOptions?: number[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UnifiedPagination',
  props: {
    totalItems: { default: 0 },
    currentPage: { default: 1 },
    itemsPerPage: { default: 25 },
    variant: { default: 'default' },
    itemLabel: { default: 'entrées' },
    showItemsPerPage: { type: Boolean, default: true },
    showFirstLastButtons: { type: Boolean, default: false },
    maxVisiblePages: { default: 3 },
    itemsPerPageOptions: { default: () => [10, 25, 50, 100] }
  },
  emits: ["update:currentPage", "update:itemsPerPage", "page-change", "items-per-page-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props

// Événements
const emit = __emit

// Computed properties
const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage))

const startIndex = computed(() => (props.currentPage - 1) * props.itemsPerPage)

const endIndex   = computed(() => Math.min(props.currentPage * props.itemsPerPage, props.totalItems))

const visiblePages = computed(() => {
  const pages: number[] = []
  const halfVisible = Math.floor(props.maxVisiblePages / 2)
  
  let start = Math.max(1, props.currentPage - halfVisible)
  let end = Math.min(totalPages.value, start + props.maxVisiblePages - 1)
  
  // Ajuster le début si on est près de la fin
  if (end - start < props.maxVisiblePages - 1) {
    start = Math.max(1, end - props.maxVisiblePages + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

// Methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit('update:currentPage', page)
    emit('page-change', page)
  }
}

const previousPage = () => {
  if (props.currentPage > 1) {
    const newPage = props.currentPage - 1
    emit('update:currentPage', newPage)
    emit('page-change', newPage)
  }
}

const nextPage = () => {
  if (props.currentPage < totalPages.value) {
    const newPage = props.currentPage + 1
    emit('update:currentPage', newPage)
    emit('page-change', newPage)
  }
}

const handleItemsPerPageChange = (event: Event) => {
  const target          = event.target as HTMLSelectElement
  const newItemsPerPage = parseInt(target.value)

  // Retourner à la première page lors du changement d'éléments par page
  emit('update:currentPage', 1)
  emit('update:itemsPerPage', newItemsPerPage)
  emit('page-change', 1)
  emit('items-per-page-change', newItemsPerPage)
}

const getButtonClasses = (type: 'nav' | 'page', isActive = false) => {
  const baseClasses = 'transition-colors'
  
  if (type === 'nav') {
    return [
      baseClasses,
      'p-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed',
      'min-w-[32px] h-8 flex items-center justify-center bg-white'
    ]
  }
  
  if (type === 'page') {
    return [
      baseClasses,
      'px-2 py-1.5 text-xs border rounded-md min-w-[32px] h-8 flex items-center justify-center',
      isActive
        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
        : 'border-gray-300 hover:bg-gray-50 text-black bg-white'
    ]
  }
  
  return baseClasses
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
    'flex flex-wrap items-center justify-between gap-2 sm:gap-3',
    _ctx.variant === 'dashboard' ? 'bg-white px-4 py-3 border-t border-gray-200' : 'mt-6 mb-2'
  ])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(startIndex.value + 1) + "-" + _toDisplayString(endIndex.value) + " sur " + _toDisplayString(_ctx.totalItems) + " " + _toDisplayString(_ctx.itemLabel), 1),
      (totalPages.value > 1)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, " (" + _toDisplayString(_ctx.currentPage) + "/" + _toDisplayString(totalPages.value) + ") ", 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      (_ctx.showItemsPerPage)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _cache[2] || (_cache[2] = _createElementVNode("label", {
              for: "pagination-items-per-page",
              class: "text-xs text-black m-0"
            }, "Afficher", -1)),
            _createElementVNode("select", {
              id: "pagination-items-per-page",
              value: _ctx.itemsPerPage,
              onChange: handleItemsPerPageChange,
              class: "px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.itemsPerPageOptions, (option) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: option,
                  value: option
                }, _toDisplayString(option), 9, _hoisted_7))
              }), 128))
            ], 40, _hoisted_6),
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-xs text-black" }, "par page", -1))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_8, [
        (_ctx.showFirstLastButtons)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (goToPage(1))),
              disabled: _ctx.currentPage === 1,
              class: _normalizeClass(getButtonClasses('nav')),
              title: "Première page"
            }, _cache[4] || (_cache[4] = [
              _createElementVNode("i", { class: "fas fa-angle-double-left text-black" }, null, -1)
            ]), 10, _hoisted_9))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          onClick: previousPage,
          disabled: _ctx.currentPage === 1,
          class: _normalizeClass(getButtonClasses('nav')),
          title: "Page précédente"
        }, _cache[5] || (_cache[5] = [
          _createElementVNode("i", { class: "fas fa-angle-left text-black" }, null, -1)
        ]), 10, _hoisted_10),
        _createElementVNode("div", _hoisted_11, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (page) => {
            return (_openBlock(), _createElementBlock("button", {
              key: page,
              onClick: ($event: any) => (goToPage(page)),
              class: _normalizeClass(getButtonClasses('page', page === _ctx.currentPage))
            }, _toDisplayString(page), 11, _hoisted_12))
          }), 128))
        ]),
        _createElementVNode("button", {
          onClick: nextPage,
          disabled: _ctx.currentPage === totalPages.value,
          class: _normalizeClass(getButtonClasses('nav')),
          title: "Page suivante"
        }, _cache[6] || (_cache[6] = [
          _createElementVNode("i", { class: "fas fa-angle-right text-black" }, null, -1)
        ]), 10, _hoisted_13),
        (_ctx.showFirstLastButtons)
          ? (_openBlock(), _createElementBlock("button", {
              key: 1,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (goToPage(totalPages.value))),
              disabled: _ctx.currentPage === totalPages.value,
              class: _normalizeClass(getButtonClasses('nav')),
              title: "Dernière page"
            }, _cache[7] || (_cache[7] = [
              _createElementVNode("i", { class: "fas fa-angle-double-right text-black" }, null, -1)
            ]), 10, _hoisted_14))
          : _createCommentVNode("", true)
      ])
    ])
  ], 2))
}
}

})